/* eslint-disable camelcase */

import ReduxService from 'src/redux/redux.service';
import { RootState } from 'src/redux/redux-store';
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type {
  FocusGroupMessage,
  FocusGroupSession,
  FocusGroupSessionDetail,
  StartSegmentRunResponse,
  SurveySegmentsResponse,
} from 'src/types/focus-group.types';

const getUrl = (url: string) => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? url.replace('b2b', 'client') : url;
};

function mapGrounding(
  grounding: { evidence: 'survey' | 'persona' | 'extrapolation'; question_ids?: string[] } | null | undefined
): FocusGroupMessage['grounding'] {
  if (!grounding) {
    return null;
  }
  return {
    evidence: grounding.evidence,
    questionIds: grounding.question_ids ?? [],
  };
}

function mapMessage(message: Record<string, unknown>): FocusGroupMessage {
  const grounding = message.grounding as {
    evidence: 'survey' | 'persona' | 'extrapolation';
    question_ids?: string[];
  } | null | undefined;
  return {
    id: String(message.id),
    role: message.role as FocusGroupMessage['role'],
    content: String(message.content ?? ''),
    inScope: Boolean(message.in_scope),
    refusal_reason: message.refusal_reason as string | null | undefined,
    grounding: mapGrounding(grounding),
    input_tokens: message.input_tokens as number | undefined,
    output_tokens: message.output_tokens as number | undefined,
    cost_usd: message.cost_usd as string | number | undefined,
    created_at: message.created_at as string | undefined,
  };
}

function mapSession(session: Record<string, unknown>): FocusGroupSession {
  return {
    id: String(session.id),
    thread_id: String(session.thread_id),
    title: String(session.title ?? ''),
    session_status: String(session.session_status ?? ''),
    turn_count: Number(session.turn_count ?? 0),
    cost_usd: session.cost_usd as string | number | undefined,
    segment_id: String(session.segment_id),
    created_at: session.created_at as string | undefined,
    suggested_openers: Array.isArray(session.suggested_openers)
      ? session.suggested_openers.map(String)
      : undefined,
  };
}

const focusGroupEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getSurveySegments: builder.query<SurveySegmentsResponse, { ext_survey_id: string }>({
      query: ({ ext_survey_id }) => ({
        method: 'get',
        url: getUrl(`b2b/survey/${ext_survey_id}/segments`),
      }),
      transformResponse(response: { item: SurveySegmentsResponse }): SurveySegmentsResponse {
        return response.item;
      },
      providesTags: (_result, _error, arg) => [
        { type: APITagType.FocusGroup, id: arg.ext_survey_id },
      ],
    }),
    startSegmentRun: builder.mutation<StartSegmentRunResponse, { ext_survey_id: string }>({
      query: ({ ext_survey_id }) => ({
        method: 'post',
        url: getUrl(`b2b/survey/${ext_survey_id}/segments`),
      }),
      transformResponse(response: { item: StartSegmentRunResponse }): StartSegmentRunResponse {
        return response.item;
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: APITagType.FocusGroup, id: arg.ext_survey_id },
      ],
    }),
    createChatSession: builder.mutation<FocusGroupSession, { segment_id: string; title?: string }>({
      query: ({ segment_id, title }) => ({
        method: 'post',
        url: `segments/${segment_id}/chat/sessions`,
        data: title ? { title } : {},
      }),
      transformResponse(response: { item: Record<string, unknown> }): FocusGroupSession {
        return mapSession(response.item);
      },
    }),
    getChatSession: builder.query<FocusGroupSessionDetail, { session_id: string }>({
      query: ({ session_id }) => ({
        method: 'get',
        url: `chat/sessions/${session_id}`,
      }),
      transformResponse(response: { item: Record<string, unknown> }): FocusGroupSessionDetail {
        const session = mapSession(response.item);
        const messages = Array.isArray(response.item.messages)
          ? response.item.messages.map((message) => mapMessage(message as Record<string, unknown>))
          : [];
        return {
          ...session,
          messages,
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: APITagType.FocusGroup, id: `session-${arg.session_id}` },
      ],
    }),
    sendChatMessage: builder.mutation<FocusGroupMessage, { session_id: string; content: string }>({
      query: ({ session_id, content }) => ({
        method: 'post',
        url: `chat/sessions/${session_id}/messages`,
        data: { content },
        handleErrorGlobally: false,
      }),
      transformResponse(response: { item: Record<string, unknown> }): FocusGroupMessage {
        return mapMessage(response.item);
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: APITagType.FocusGroup, id: `session-${arg.session_id}` },
      ],
    }),
  }),
});

export const {
  useGetSurveySegmentsQuery,
  useStartSegmentRunMutation,
  useCreateChatSessionMutation,
  useGetChatSessionQuery,
  useSendChatMessageMutation,
} = focusGroupEndpoints;

export { focusGroupEndpoints };
